import { getUnknownErrorMessage } from '@caresend/utils';

/**
 * Gives every promise a chance. Inspired by `tryAll` in cloud.
 *
 * Takes an array of functions that return promises, and runs them all with
 * Promise.all, but each individually wrapped in a try/catch. Errors will be
 * logged as they are encountered. Once all promises have been resolved or
 * rejected, if there were any errors, they will be thrown as a single error.
 *
 * To avoid uncaught exceptions, it is important that we pass in an array of
 * functions that return promises, so that the execution of the promises doesn't
 * start until they have been safely wrapped in a try/catch.
 *
 * Useful for a scenario where multiple unrelated promises need to run without
 * affecting each other.
 *
 * Can be used to return multiple typed values in a tuple type with `as const`.
 *
 * Example:
 * ```
 *       ^ string       ^ number
 * const [stringResult, numberResult] = await tryAllBrowser([
 *  () => Promise.resolve('string'),
 *  () => Promise.resolve(1),
 * ] as const);
 * ```
 */
const tryAllBrowser = async (promiseFunctions) => {
    const errors = [];
    const tryWrapped = promiseFunctions.map(async (func) => {
        try {
            return await func();
        }
        catch (error) {
            console.error(error);
            errors.push(error);
        }
    });
    const awaited = await Promise.all(tryWrapped);
    if (errors.length === 1) {
        throw errors[0];
    }
    else if (errors.length) {
        const messages = errors.map((error) => getUnknownErrorMessage(error));
        throw Error(messages.join(', '));
    }
    return awaited;
};

export { tryAllBrowser };
